import React from 'react'
import {
  ShieldCheck,
  ShieldAlert,
  ShieldQuestion,
  CalendarX,
  BookOpen
} from 'lucide-react'
import contractReportsService from '../../../services/contractReportsService'
import {
  ReportShell,
  ReportSection,
  ReportTable,
  KpiGrid,
  EmptyState,
  StatusBadge,
  useContractReport,
  toInputDate,
  formatNumber,
  formatPercent,
  formatDate,
  primaryCurrency,
  contractColumns,
  withExportOnly,
  breakdownColumns,
  breakdownFooter,
  summarySection
} from './reportShared'

const DEFAULT_FILTERS = { asOf: toInputDate() }

const reviewCols = withExportOnly(
  [
    contractColumns.contract,
    contractColumns.party,
    contractColumns.status,
    {
      key: 'complianceStatus',
      label: 'Compliance',
      render: (r) => <StatusBadge status={r.complianceStatus} />
    },
    contractColumns.value,
    {
      key: 'lastComplianceReview',
      label: 'Last Review',
      render: (r) => formatDate(r.lastComplianceReview),
      exportValue: (r) => formatDate(r.lastComplianceReview)
    },
    {
      key: 'nextComplianceReview',
      label: 'Next Review',
      render: (r) => (
        <span style={{ color: r.reviewOverdueDays > 0 ? '#b91c1c' : undefined }}>
          {formatDate(r.nextComplianceReview)}
          {r.reviewOverdueDays > 0 && <span className="contract-report-subtext">{r.reviewOverdueDays}d overdue</span>}
        </span>
      ),
      exportValue: (r) => formatDate(r.nextComplianceReview)
    },
    {
      key: 'requirementsCount',
      label: 'Requirements',
      align: 'right',
      render: (r) => formatNumber(r.requirementsCount)
    }
  ],
  [contractColumns.title, contractColumns.currency, { key: 'reviewOverdueDays', label: 'Days Overdue' }]
)

const categoryCols = [
  { key: 'category', label: 'Category' },
  { key: 'count', label: 'Active Rules', align: 'right', render: (r) => formatNumber(r.count) }
]

const priorityCols = [
  { key: 'priority', label: 'Priority' },
  { key: 'count', label: 'Active Rules', align: 'right', render: (r) => formatNumber(r.count) }
]

const ContractComplianceReport = () => {
  const report = useContractReport(contractReportsService.getComplianceReport, DEFAULT_FILTERS)

  const getExportSections = (data) => {
    const cur = primaryCurrency(data.meta)
    const s = data.summary
    return [
      summarySection('Summary', [
        ['Total contracts', s.totalContracts],
        ['Open contracts', s.openContracts],
        ['Compliant', s.compliant],
        ['Non-compliant', s.nonCompliant],
        ['Under review', s.underReview],
        ['Not applicable', s.notApplicable],
        ['Not set', s.notSet],
        ['Compliance rate (%)', s.complianceRate ?? 'N/A'],
        ['Overdue reviews', s.overdueReviews],
        ['Reviews due in 30 days', s.reviewsDueIn30],
        ['Active compliance rules', data.rules.activeRules],
        ['Coverage note', data.coverage.coverageNote || '']
      ]),
      {
        title: 'By Compliance Status',
        columns: breakdownColumns('complianceStatus', 'Compliance Status', s.byComplianceStatus, cur),
        rows: s.byComplianceStatus
      },
      { title: 'Overdue Reviews', columns: reviewCols.export, rows: data.overdueReviews },
      { title: 'Reviews Due 30d', columns: reviewCols.export, rows: data.upcomingReviews },
      { title: 'Non-Compliant', columns: reviewCols.export, rows: data.nonCompliant },
      { title: 'Under Review', columns: reviewCols.export, rows: data.underReview },
      { title: 'Rules by Category', columns: categoryCols, rows: data.rules.byCategory },
      { title: 'Rules by Priority', columns: priorityCols, rows: data.rules.byPriority }
    ]
  }

  return (
    <ReportShell
      title="Compliance Reports"
      subtitle="Contract compliance status, review schedule and rule coverage"
      path="/contracts/reports/compliance"
      report={report}
      filterFields={['asOf']}
      filterHint="Overdue and upcoming reviews are measured from the as-of date"
      exportName="Contract_Compliance"
      getExportSections={getExportSections}
    >
      {(data) => {
        const s = data.summary
        const cur = primaryCurrency(data.meta)
        if (s.totalContracts === 0 && data.rules.totalRules === 0) {
          return <EmptyState message="No signed contracts or compliance rules found" />
        }
        return (
          <>
            {data.coverage.coverageNote && (
              <div className="contract-report-note info">
                <span><strong>Data coverage:</strong> {data.coverage.coverageNote}</span>
              </div>
            )}

            <KpiGrid
              items={[
                {
                  icon: ShieldCheck,
                  label: 'Compliance Rate',
                  value: formatPercent(s.complianceRate),
                  subtitle: `${formatNumber(s.compliant)} compliant (excl. N/A)`
                },
                {
                  icon: ShieldAlert,
                  label: 'Non-Compliant',
                  value: formatNumber(s.nonCompliant),
                  badge: s.nonCompliant > 0 ? 'Action needed' : undefined,
                  badgeTone: 'negative',
                  subtitle: 'Contracts flagged'
                },
                {
                  icon: ShieldQuestion,
                  label: 'Under Review',
                  value: formatNumber(s.underReview),
                  subtitle: `${formatNumber(s.notApplicable)} not applicable`
                },
                {
                  icon: CalendarX,
                  label: 'Overdue Reviews',
                  value: formatNumber(s.overdueReviews),
                  subtitle: `${formatNumber(s.reviewsDueIn30)} due in next 30 days`
                }
              ]}
            />

            <div className="contract-report-grid-2">
              <ReportSection title="By Compliance Status" subtitle="All signed contracts">
                <ReportTable
                  columns={breakdownColumns('complianceStatus', 'Compliance Status', s.byComplianceStatus, cur, { badge: true })}
                  rows={s.byComplianceStatus}
                  footer={breakdownFooter('complianceStatus', s.byComplianceStatus, cur)}
                  emptyText="No signed contracts"
                />
              </ReportSection>
              <ReportSection
                title="Compliance Rules"
                subtitle="Active rules in Compliance Rule Master"
                count={`${data.rules.activeRules} active of ${data.rules.totalRules}`}
              >
                <ReportTable columns={categoryCols} rows={data.rules.byCategory} emptyText="No active compliance rules" />
                {data.rules.byPriority.length > 0 && (
                  <div style={{ borderTop: '1px solid #eef0f4' }}>
                    <ReportTable columns={priorityCols} rows={data.rules.byPriority} />
                  </div>
                )}
              </ReportSection>
            </div>

            <ReportSection
              title="Overdue Compliance Reviews"
              subtitle="Open contracts whose next review date is before the as-of date"
              count={s.overdueReviews > data.overdueReviews.length ? `Top ${data.overdueReviews.length} of ${s.overdueReviews}` : `${s.overdueReviews} contracts`}
            >
              <ReportTable columns={reviewCols.display} rows={data.overdueReviews} emptyText="No overdue reviews" />
            </ReportSection>

            <ReportSection
              title="Reviews Due in Next 30 Days"
              count={`${data.upcomingReviews.length} contracts`}
            >
              <ReportTable columns={reviewCols.display} rows={data.upcomingReviews} emptyText="No reviews scheduled in the next 30 days" />
            </ReportSection>

            <ReportSection title="Non-Compliant Contracts" subtitle="Top 50 by value" count={`${s.nonCompliant} contracts`}>
              <ReportTable columns={reviewCols.display} rows={data.nonCompliant} emptyText="No non-compliant contracts" />
            </ReportSection>

            <ReportSection title="Contracts Under Review" subtitle="Top 50 by value" count={`${s.underReview} contracts`}>
              <ReportTable columns={reviewCols.display} rows={data.underReview} emptyText="No contracts under review" />
            </ReportSection>

            {data.rules.activeRules === 0 && (
              <div className="contract-report-note info">
                <BookOpen size={14} style={{ flexShrink: 0, marginTop: 1 }} />
                <span>No active compliance rules are configured. Add rules under Contracts → Masters → Compliance Rules.</span>
              </div>
            )}
          </>
        )
      }}
    </ReportShell>
  )
}

export default ContractComplianceReport
